/**
 * On-call doctor mobile view — /oncall?room=…&token=…
 *
 * Opened from the escalation SMS on a phone, by a doctor who is not signed in
 * and may be walking, driving-just-stopped, or half asleep. It is deliberately
 * NOT a small clinician console: one screen, one primary action, thumb-sized
 * targets, and nothing that needs a second tap to understand.
 *
 * What it shows, in the order a doctor answering a page actually wants it:
 *   1. how urgent, and how long they have
 *   2. the patient, live
 *   3. allergies — the one piece of history that changes what you do first
 *   4. the three vitals that matter
 *   5. one button: take over and talk
 *
 * It joins as the `oncall` role, which sits ALONGSIDE the desktop console
 * rather than replacing it — the console operator is a different person and
 * stays on the consult.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createAudioContext, resumeAudioContext, createSpeakerRoute } from '../audioContext';

const ATS_LABEL = { 1: 'Resuscitation', 2: 'Emergency', 3: 'Urgent', 4: 'Semi-urgent', 5: 'Non-urgent' };
const ATS_COLOUR = { 1: '#ef4444', 2: '#f97316', 3: '#eab308', 4: '#22c55e', 5: '#22c55e' };

function useQuery() {
  const p = new URLSearchParams(window.location.search);
  return {
    room: p.get('room') || 'default',
    // `t` is the current form; `token` is still accepted so a link already sent
    // to a doctor's phone does not stop working mid-page.
    token: p.get('t') || p.get('token') || ''
  };
}

function fmtTime(iso) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function fmtClock(totalSeconds) {
  if (totalSeconds == null || totalSeconds < 0) return null;
  const m = Math.floor(totalSeconds / 60);
  const s = Math.floor(totalSeconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function OnCallView() {
  const { room, token } = useQuery();

  const [phase, setPhase] = useState('checking');   // checking | denied | ready | live | ended
  const [error, setError] = useState(null);
  const [escalation, setEscalation] = useState(null);
  const [triage, setTriage] = useState(null);
  const [vitals, setVitals] = useState({ bpm: null, breathingRate: null, spo2: null });
  const [vitalsStatus, setVitalsStatus] = useState(null);
  const [record, setRecord] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [inControl, setInControl] = useState(false);
  const [matildaSpeaking, setMatildaSpeaking] = useState(false);
  const [etaLeft, setEtaLeft] = useState(null);
  const [patientPresent, setPatientPresent] = useState(true);
  const [handover, setHandover] = useState(null);          // the ISBAR, rendered
  const [pane, setPane] = useState('handover');            // handover | conversation
  const [openSection, setOpenSection] = useState('situation');

  const wsRef = useRef(null);
  const imgRef = useRef(null);
  const lastUrlRef = useRef(null);
  const playbackCtxRef = useRef(null);
  const speakerAudioRef = useRef(null);
  const speakerRouteRef = useRef(createSpeakerRoute('oncall'));
  const playbackNextRef = useRef(0);
  const micCtxRef = useRef(null);
  const micNodeRef = useRef(null);
  const micSourceRef = useRef(null);
  const micStreamRef = useRef(null);

  // --- audio out (patient + MATILDA) ---------------------------------------
  //
  // iOS routes raw AudioContext output to the EARPIECE, not the loudspeaker —
  // and once the doctor taps "Take over consult" the page holds a live mic
  // capture, which pins the whole audio session to the receiver. A doctor being
  // paged has the phone in their hand, not against their ear. The route lives
  // in audioContext.js and is shared with the patient view.
  const playPcm16 = useCallback((base64, sampleRate = 24000) => {
    if (!playbackCtxRef.current) playbackCtxRef.current = createAudioContext({ sampleRate: 24000 });
    const ctx = playbackCtxRef.current;
    if (!ctx) return;
    resumeAudioContext(ctx);
    // A no-op once armed or refused; the real arming happens in the join tap.
    speakerRouteRef.current.arm(ctx, speakerAudioRef.current);
    try {
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const buffer = ctx.createBuffer(1, bytes.length / 2, sampleRate);
      const ch = buffer.getChannelData(0);
      for (let i = 0; i < bytes.length; i += 2) {
        const sample = bytes[i] | (bytes[i + 1] << 8);
        ch[i / 2] = sample > 32767 ? (sample - 65536) / 32768 : sample / 32768;
      }
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.connect(speakerRouteRef.current.output(ctx));
      const now = ctx.currentTime;
      if (playbackNextRef.current < now) playbackNextRef.current = now + 0.02;
      src.start(playbackNextRef.current);
      playbackNextRef.current += buffer.duration;
    } catch (err) {
      console.error('audio playback:', err);
    }
  }, []);

  // --- mic in ---------------------------------------------------------------
  const startMic = useCallback(async () => {
    if (micCtxRef.current) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, sampleRate: 24000, channelCount: 1 },
        video: false
      });
      const ctx = createAudioContext({ sampleRate: 24000 });
      if (!ctx) return;
      await ctx.audioWorklet.addModule('/pcm16-worklet.js');
      const source = ctx.createMediaStreamSource(stream);
      const node = new AudioWorkletNode(ctx, 'pcm16-downsampler');
      node.port.onmessage = (event) => {
        const buf = event.data;
        const ws = wsRef.current;
        if (!buf || !buf.byteLength || !ws || ws.readyState !== WebSocket.OPEN) return;
        const bytes = new Uint8Array(buf);
        let bin = '';
        for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
        ws.send(JSON.stringify({ type: 'audio_chunk', speaker: 'clinician', data: btoa(bin) }));
      };
      source.connect(node);
      micCtxRef.current = ctx;
      micNodeRef.current = node;
      micSourceRef.current = source;
      micStreamRef.current = stream;
    } catch (err) {
      setError('Microphone unavailable — check the browser permission.');
    }
  }, []);

  const stopMic = useCallback(() => {
    if (micNodeRef.current) { micNodeRef.current.port.close(); micNodeRef.current = null; }
    if (micSourceRef.current) { micSourceRef.current.disconnect(); micSourceRef.current = null; }
    if (micCtxRef.current) { micCtxRef.current.close(); micCtxRef.current = null; }
    if (micStreamRef.current) { micStreamRef.current.getTracks().forEach(t => t.stop()); micStreamRef.current = null; }
  }, []);

  // --- validate the link before opening a socket ---------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) { setPhase('denied'); setError('This link is incomplete. Ask for a new one.'); return; }
      try {
        const res = await fetch('/api/oncall-link/check', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, room })
        });
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok || !data.ok) { setPhase('denied'); setError(data.error || 'This link is no longer valid.'); return; }
        setEscalation(data.clinician ? { clinician: data.clinician } : null);
        if (data.triageScore != null) setTriage(data.triageScore);
        setPhase('ready');
      } catch {
        if (!cancelled) { setPhase('denied'); setError('Could not reach MATILDA. Check your connection.'); }
      }
    })();
    return () => { cancelled = true; };
  }, [room, token]);

  // --- connect (after an explicit tap, so audio is allowed to play) --------
  const connect = useCallback(() => {
    if (wsRef.current) return;
    // A user gesture is required before a phone will let us play audio.
    if (!playbackCtxRef.current) playbackCtxRef.current = createAudioContext({ sampleRate: 24000 });
    if (!playbackCtxRef.current) return;
    resumeAudioContext(playbackCtxRef.current);
    // Arm the loudspeaker route here, in the tap. Doing it on the first audio
    // chunk (as before) ran outside any user gesture: iOS rejected play(), the
    // route stayed armed, and the doctor heard nothing from the patient.
    speakerRouteRef.current.allowRetry();
    speakerRouteRef.current.arm(playbackCtxRef.current, speakerAudioRef.current);

    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${proto}//${window.location.host}`);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'join', role: 'oncall', room, inviteToken: token }));
      setPhase('live');
    };

    ws.onmessage = (event) => {
      if (event.data instanceof Blob) {
        const url = URL.createObjectURL(event.data);
        if (imgRef.current) imgRef.current.src = url;
        if (lastUrlRef.current) URL.revokeObjectURL(lastUrlRef.current);
        lastUrlRef.current = url;
        return;
      }
      let msg;
      try { msg = JSON.parse(event.data); } catch { return; }

      switch (msg.type) {
        case 'auth-error':
          setPhase('denied'); setError(msg.message); ws.close(); break;
        case 'oncall_snapshot':
          if (msg.vitals) setVitals(v => ({ ...v, ...msg.vitals }));
          if (msg.triage != null) setTriage(msg.triage);
          if (msg.healthRecord) setRecord(msg.healthRecord);
          if (Array.isArray(msg.transcript)) setTranscript(msg.transcript);
          if (msg.isbar?.sections) setHandover(msg.isbar);
          if (msg.escalation) {
            setEscalation(msg.escalation);
            if (msg.escalation.etaSeconds && msg.escalation.etaStartedAt) {
              const elapsed = (Date.now() - msg.escalation.etaStartedAt) / 1000;
              setEtaLeft(Math.max(0, msg.escalation.etaSeconds - elapsed));
            }
          }
          break;
        // Escalation steps are replayed on join, but this view deliberately does
        // not show the agent timeline — the console does. Kept out of state so
        // the phone stays one screen with one action.
        // The document can finish generating after the doctor has already
        // joined — the escalation pushes it the moment it is ready.
        case 'isbar_ready':
          setHandover({ sections: msg.sections, filename: msg.filename, generatedAt: msg.generatedAt });
          break;
        case 'transcript':
          setTranscript(prev => [...prev, msg].slice(-40));
          break;
        case 'transcript_correction':
          setTranscript(prev => prev.map(t =>
            t.timestamp === msg.timestamp && t.speaker === 'matilda' ? { ...t, text: msg.text } : t));
          break;
        case 'audio_chunk':
          if (msg.speaker === 'patient' || msg.speaker === 'matilda') playPcm16(msg.data, msg.sampleRate || 24000);
          break;
        case 'matilda_speaking':
          setMatildaSpeaking(!!msg.speaking); break;
        case 'matilda_voice_state':
          // The console took control back while we were holding it.
          if (msg.by !== 'oncall') {
            setInControl(!msg.enabled);
            if (msg.enabled) stopMic();
          }
          break;
        case 'patient_left':
        case 'peer-left':
          if (msg.role !== 'clinician') setPatientPresent(false);
          break;
        case 'peer-joined':
          if (msg.role === 'patient') setPatientPresent(true);
          break;
        case 'session_ended':
          setPhase('ended'); break;
        default:
          if (msg.integerValue !== undefined || msg.breathingRate !== undefined || msg.spo2 !== undefined) {
            setVitals(prev => ({
              bpm: msg.integerValue || prev.bpm,
              breathingRate: msg.breathingRate || prev.breathingRate,
              spo2: msg.spo2 || prev.spo2
            }));
            if (msg.vitalsStatus) setVitalsStatus(msg.vitalsStatus);
          }
      }
    };

    ws.onclose = () => { wsRef.current = null; setPhase(p => (p === 'denied' || p === 'ended' ? p : 'ready')); };
  }, [room, token, playPcm16, stopMic]);

  useEffect(() => () => {
    stopMic();
    // The route's nodes belong to the context being closed and die with it.
    speakerRouteRef.current.reset(speakerAudioRef.current);
    if (playbackCtxRef.current) {
      try { playbackCtxRef.current.close(); } catch { /* already closed */ }
      playbackCtxRef.current = null;
    }
    if (wsRef.current) wsRef.current.close();
    if (lastUrlRef.current) URL.revokeObjectURL(lastUrlRef.current);
  }, [stopMic]);

  // ETA countdown
  useEffect(() => {
    if (etaLeft == null) return;
    const id = setInterval(() => setEtaLeft(v => (v == null ? null : Math.max(0, v - 1))), 1000);
    return () => clearInterval(id);
  }, [etaLeft != null]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- the one action -------------------------------------------------------
  const toggleControl = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const takingOver = !inControl;
    // matilda_voice enabled=false means the human is talking.
    ws.send(JSON.stringify({ type: 'matilda_voice', enabled: !takingOver }));
    setInControl(takingOver);
    if (takingOver) startMic(); else stopMic();
  }, [inControl, startMic, stopMic]);

  // --- render ---------------------------------------------------------------
  const spo2 = typeof vitals.spo2 === 'object' ? vitals.spo2?.value : vitals.spo2;
  const allergies = record?.clinicalSections?.allergies || [];
  const patientName = record?.patient?.name
    ? `${record.patient.name.given || ''} ${record.patient.name.family || ''}`.trim()
    : null;
  const hrSettling = vitalsStatus && !vitalsStatus.hr?.settled;
  const rrSettling = vitalsStatus && !vitalsStatus.br?.settled;
  const rrUnreliable = vitalsStatus?.br?.unreliable || null;
  const hrUnreliable = vitalsStatus?.hr?.unreliable || null;

  // The phase decides the body; the hidden <audio> is rendered ONCE, outside
  // the switch, so it is mounted in every phase and React keeps the same DOM
  // element across the splash -> live change. It has to exist on the splash:
  // the join tap is where iOS lets us start it playing, and the live screen
  // only appears after the socket opens, outside that gesture. iOS plays
  // <audio> through the loudspeaker; a bare AudioContext goes to the earpiece.
  let body;
  if (phase === 'checking') body = <Splash>Checking your link…</Splash>;
  else if (phase === 'denied') body = <Splash tone="bad" title="Link not valid">{error}</Splash>;
  else if (phase === 'ended') body = <Splash title="Consult ended">This consult has been closed.</Splash>;
  else if (phase === 'ready') {
    body = (
      <Splash
        title={triage ? `ATS ${triage} — ${ATS_LABEL[triage] || 'Urgent'}` : 'RFDS consult'}
        tone={triage <= 2 ? 'bad' : 'normal'}
      >
        <p style={{ color: MUTED, fontSize: '15px', lineHeight: 1.5, margin: '0 0 24px' }}>
          {escalation?.clinician?.name
            ? `You are being paged as ${escalation.clinician.name}.`
            : 'You are being asked to review a remote consult.'}
        </p>
        <button onClick={connect} style={primaryBtn}>Join consult</button>
      </Splash>
    );
  } else body = (
    <div style={shell}>
      {/* 1. urgency + time */}
      <header style={header}>
        <div style={accentBlue} />
        <div style={headerRow}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
            <img src="/logos/rfds-logo.png" alt="RFDS"
              style={{ height: '26px', width: 'auto', flexShrink: 0 }}
              onError={e => { e.currentTarget.style.display = 'none'; }} />
            <span style={{
              width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0,
              background: ATS_COLOUR[triage] || '#6b7590'
            }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '16px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {patientName || 'Patient'}
              </div>
              <div style={{ fontSize: '13px', color: MUTED, marginTop: '2px' }}>
                {triage ? `ATS ${triage} — ${ATS_LABEL[triage]}` : 'Awaiting triage'}
              </div>
            </div>
          </div>
          {etaLeft != null && (
            <span style={{
              color: etaLeft < 60 ? '#ff6b7a' : MUTED, fontSize: '17px', fontWeight: 600,
              fontVariantNumeric: 'tabular-nums', flexShrink: 0
            }}>
              {fmtClock(etaLeft)}
            </span>
          )}
        </div>
      </header>

      {/* 2. the patient */}
      <div style={videoWrap}>
        {/* Mirrored, matching the desktop console — both clinicians then see the
            patient the same way round as the patient sees themselves. */}
        <img ref={imgRef} alt="Patient video" style={video} />
        {!patientPresent && <div style={videoOverlay}>Patient has left the consult</div>}
        {matildaSpeaking && !inControl && (
          <div style={speakingPill}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#c4b5fd' }} />
            MATILDA speaking
          </div>
        )}
      </div>

      {/* 3. allergies — first thing that changes management */}
      {allergies.length > 0 && (
        <div style={allergyCard}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ff6b7a' }} />
            <strong style={{ fontSize: '13px', letterSpacing: '0.3px', color: '#ff6b7a' }}>ALLERGIES</strong>
          </div>
          <div style={{ color: '#fff' }}>
            {allergies.map(a => a.substance?.display || a.substance).join(', ')}
          </div>
        </div>
      )}

      {/* 4. the three that matter */}
      <div style={vitalCard}>
        <Vital label="HR" value={vitals.bpm} unit="bpm" settling={hrSettling}
          unavailable={!!hrUnreliable}
          bad={v => v > 130 || v < 40} warn={v => v > 110 || v < 50} />
        <Vital label="RR" value={vitals.breathingRate} unit="br/m" settling={rrSettling}
          unavailable={!!rrUnreliable}
          bad={v => v >= 25 || v <= 8} warn={v => v >= 21 || v <= 11} />
        <Vital label="SpO₂" value={spo2} unit="%" bad={v => v <= 91} warn={v => v <= 95} />
      </div>

      {/* 5. the handover, and the conversation behind it.
             Two panes rather than two cards: a phone has one screen, and the
             doctor is either reading the brief or listening to the consult. */}
      <div style={paneCard}>
        <div style={accentBlue} />
        <div style={tabRow} role="tablist">
          <button role="tab" aria-selected={pane === 'handover'} onClick={() => setPane('handover')}
            style={pane === 'handover' ? tabOn : tabOff}>
            ISBAR handover
          </button>
          <button role="tab" aria-selected={pane === 'conversation'} onClick={() => setPane('conversation')}
            style={pane === 'conversation' ? tabOn : tabOff}>
            Conversation
          </button>
        </div>

        <div style={paneBody}>
          {pane === 'handover' ? (
            handover?.sections?.length ? (
              <>
                {handover.generatedAt && (
                  <div style={stampRow}>
                    Written at escalation, {fmtTime(handover.generatedAt)} — the vitals above are live
                  </div>
                )}
                {handover.sections.map(sec => {
                  const open = openSection === sec.key;
                  return (
                    <div key={sec.key} style={sectionRow}>
                      <button
                        onClick={() => setOpenSection(open ? null : sec.key)}
                        aria-expanded={open}
                        style={sectionHead}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '9px', minWidth: 0 }}>
                          <span style={sectionInitial}>{sec.title[0]}</span>
                          <span style={{ fontSize: '15px', fontWeight: 600, color: TEXT }}>{sec.title}</span>
                          <span style={{ fontSize: '12px', color: DIM, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {sec.hint}
                          </span>
                        </span>
                        <span style={{ color: MUTED, fontSize: '13px', flexShrink: 0 }}>{open ? '▾' : '▸'}</span>
                      </button>
                      {open && <div style={sectionBody}>{sec.text}</div>}
                    </div>
                  );
                })}
                {/* The .docx is the artefact that goes on the record; the text
                    above is the same content, readable without an app.
                    The clinician route needs a sign-in this doctor does not
                    have, so the invite token authorises this one instead. */}
                {handover.filename && (
                  <a
                    href={`/api/oncall/${encodeURIComponent(room)}/isbar?t=${encodeURIComponent(token)}`}
                    download={handover.filename}
                    style={docLink}
                  >
                    Download the .docx handover
                  </a>
                )}
              </>
            ) : (
              <span style={{ color: DIM }}>
                No handover yet — MATILDA writes the ISBAR when the consult escalates.
              </span>
            )
          ) : (
            transcript.length === 0
              ? <span style={{ color: DIM }}>No conversation yet</span>
              : transcript.slice(-12).map((t, i) => (
                <div key={i} style={{ marginBottom: '6px' }}>
                  <span style={{ color: t.speaker === 'patient' ? '#60a5fa' : '#c4b5fd', fontWeight: 600 }}>
                    {t.speaker === 'patient' ? 'Patient' : t.speaker === 'matilda' ? 'MATILDA' : 'Clinician'}
                  </span>
                  <span style={{ color: '#d7dae3' }}> {t.text}</span>
                </div>
              ))
          )}
        </div>
      </div>

      {error && <div style={{ color: '#ff6b7a', fontSize: '14px', padding: '0 4px' }}>{error}</div>}

      {/* 6. one button. Blue is the primary action across MATILDA — red is
             reserved for ending a call, which this is not. */}
      <div style={{ flexShrink: 0 }}>
        <button onClick={toggleControl} style={{
          ...primaryBtn,
          background: inControl ? '#6ee7a8' : ACCENT,
          color: inControl ? '#0b2a1a' : '#fff'
        }}>
          {inControl ? '🎙  You are speaking — tap to hand back' : 'Take over consult'}
        </button>
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '10px' }}>
          <img src="/logos/uncapt-logo-white.svg" alt="UNCAPT"
            style={{ height: '11px', width: 'auto', opacity: 0.4 }}
            onError={e => { e.currentTarget.style.display = 'none'; }} />
        </div>
      </div>
    </div>
  );

  return (
    <>
      <audio ref={speakerAudioRef} autoPlay playsInline style={{ display: 'none' }} />
      {body}
    </>
  );
}

// --- small pieces ----------------------------------------------------------

function Vital({ label, value, unit, bad, warn, settling, unavailable }) {
  const colour = value == null || settling || unavailable ? DIM
    : bad(value) ? '#ff6b7a' : warn(value) ? '#ffd166' : '#6ee7a8';
  return (
    <div style={{ flex: 1, textAlign: 'center', padding: '14px 4px' }}>
      <div style={{ fontSize: '13px', color: MUTED, fontWeight: 500 }}>{label}</div>
      {unavailable ? (
        <div style={{ fontSize: '13px', color: '#ffd166', marginTop: '6px' }}>check manually</div>
      ) : settling ? (
        <div style={{ fontSize: '14px', color: DIM, marginTop: '6px' }}>calibrating</div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '4px', marginTop: '4px' }}>
          <span style={{ fontSize: '28px', fontWeight: 600, color: colour, fontVariantNumeric: 'tabular-nums', lineHeight: 1.15 }}>
            {value != null ? value : '—'}
          </span>
          <span style={{ fontSize: '12px', color: DIM }}>{unit}</span>
        </div>
      )}
    </div>
  );
}

function Splash({ title, tone, children }) {
  return (
    <div style={{ ...shell, overflowY: 'auto', justifyContent: 'center', alignItems: 'center', padding: '32px 24px', textAlign: 'center' }}>
      <img src="/logos/rfds-logo.png" alt="Royal Flying Doctor Service"
        style={{ height: '44px', width: 'auto', marginBottom: '20px' }}
        onError={e => { e.currentTarget.style.display = 'none'; }} />
      {title && (
        <h1 style={{ fontSize: '22px', margin: '0 0 12px', color: tone === 'bad' ? '#ff6b7a' : TEXT, fontWeight: 600 }}>
          {title}
        </h1>
      )}
      <div style={{ color: MUTED, fontSize: '15px', lineHeight: 1.5, maxWidth: '380px', width: '100%' }}>{children}</div>
      <img src="/logos/uncapt-logo-white.svg" alt="UNCAPT"
        style={{ height: '13px', width: 'auto', marginTop: '44px', opacity: 0.5 }}
        onError={e => { e.currentTarget.style.display = 'none'; }} />
    </div>
  );
}

// --- styles ---------------------------------------------------------------
//
// Matches the patient view's language rather than inventing a second one: a
// #151521 page carrying rounded #1e1e2d cards, 16px radius, 14-16px type. The
// first cut of this screen was the clinician console shrunk down — flat
// sections, hairline dividers, 9-11px labels — which reads as dense on a phone
// where the patient side reads as calm.

const PAGE = '#151521';
const CARD = '#1e1e2d';
const TEXT = '#fff';
const MUTED = '#8b93a7';
const DIM = '#6b7590';
// The patient view carries one saturated colour and it is blue: #2563eb for
// the primary action, #60a5fa for the hairline that tops each card. This screen
// had neither — a red button on flat charcoal — which read as a different
// product. The surfaces were always #151521/#1e1e2d; the black was the video
// backdrop and the absence of any accent at all.
const ACCENT = '#2563eb';
const ACCENT_LIGHT = '#60a5fa';

const shell = {
  // A FIXED height, not a minimum. With min-height alone the container has no
  // definite size, so `flex: 1` on the pane below grows to fit its content
  // instead of scrolling inside it — which pushed "Take over consult", the one
  // action on this screen, below the fold as soon as the handover arrived.
  height: '100dvh',
  minHeight: '100dvh',
  boxSizing: 'border-box',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  padding: '12px',
  paddingTop: 'calc(12px + env(safe-area-inset-top))',
  paddingBottom: 'calc(12px + env(safe-area-inset-bottom))',
  background: PAGE,
  color: TEXT,
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  overflowX: 'hidden'
};

const card = {
  background: CARD,
  borderRadius: '16px',
  overflow: 'hidden',
  flexShrink: 0
};

// The hairline the patient view puts on every card.
const accentBlue = {
  height: '2px',
  background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_LIGHT} 55%, rgba(96,165,250,0))`
};

const header = { ...card };
const headerRow = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  gap: '12px', padding: '13px 16px'
};

const videoWrap = { ...card, position: 'relative', width: '100%', aspectRatio: '4 / 3', background: CARD };
const video = {
  width: '100%', height: '100%', objectFit: 'contain', display: 'block',
  // Same handedness as the desktop console, which mirrors the feed so the
  // clinician sees the patient as the patient sees themselves.
  transform: 'scaleX(-1)'
};
const videoOverlay = {
  position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'rgba(21,21,33,0.82)', color: '#ffd166', fontSize: '15px', fontWeight: 600
};
const speakingPill = {
  position: 'absolute', left: '12px', bottom: '12px',
  display: 'flex', alignItems: 'center', gap: '7px',
  background: 'rgba(21,21,33,0.85)', backdropFilter: 'blur(6px)',
  color: '#c4b5fd', fontSize: '12px', fontWeight: 600,
  padding: '7px 12px', borderRadius: '999px'
};

// Allergies keep a red card of their own — it is the one thing on this screen
// that changes what the doctor does first.
const allergyCard = {
  ...card,
  background: 'rgba(220,53,69,0.12)',
  border: '1px solid rgba(220,53,69,0.32)',
  padding: '13px 16px',
  color: '#ff9aa4',
  fontSize: '14px',
  lineHeight: 1.45
};

const vitalCard = { ...card, display: 'flex', padding: '4px 0' };

const rrNoticeCard = {
  ...card,
  background: 'rgba(234,179,8,0.10)',
  border: '1px solid rgba(234,179,8,0.28)',
  padding: '11px 14px',
  color: '#e2c76d',
  fontSize: '13px',
  lineHeight: 1.45
};

const paneCard = {
  ...card,
  flex: 1, minHeight: '150px',
  display: 'flex', flexDirection: 'column'
};

const tabRow = {
  display: 'flex', gap: '4px', padding: '8px 8px 0',
  flexShrink: 0
};

const tabBase = {
  flex: 1, border: 'none', borderRadius: '10px', padding: '9px 8px',
  fontSize: '13px', fontWeight: 600, fontFamily: 'inherit',
  cursor: 'pointer', touchAction: 'manipulation'
};
const tabOn = { ...tabBase, background: 'rgba(37,99,235,0.22)', color: '#bfd4ff' };
const tabOff = { ...tabBase, background: 'transparent', color: MUTED };

const paneBody = {
  flex: 1, minHeight: 0, overflowY: 'auto',
  padding: '10px 14px 14px', fontSize: '14px', lineHeight: 1.5,
  WebkitOverflowScrolling: 'touch'
};

// --- ISBAR ----------------------------------------------------------------
const sectionRow = { borderTop: '1px solid rgba(255,255,255,0.06)' };
const sectionHead = {
  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  gap: '10px', background: 'transparent', border: 'none', padding: '12px 2px',
  fontFamily: 'inherit', textAlign: 'left', cursor: 'pointer', touchAction: 'manipulation'
};
// I S B A R down the left edge — the doctor knows the mnemonic, so the letters
// do the labelling work that a heading would otherwise need.
const sectionInitial = {
  width: '22px', height: '22px', borderRadius: '6px', flexShrink: 0,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  background: 'rgba(37,99,235,0.22)', color: '#bfd4ff',
  fontSize: '12px', fontWeight: 700
};
const sectionBody = {
  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
  color: '#d7dae3', fontSize: '14px', lineHeight: 1.55,
  padding: '0 2px 14px 33px'
};
const stampRow = {
  color: DIM, fontSize: '12px', lineHeight: 1.4,
  padding: '2px 2px 10px'
};

const docLink = {
  display: 'block', marginTop: '14px', padding: '12px',
  borderRadius: '10px', background: 'rgba(255,255,255,0.05)',
  color: ACCENT_LIGHT, fontSize: '13px', fontWeight: 600,
  textAlign: 'center', textDecoration: 'none'
};

const primaryBtn = {
  width: '100%', minHeight: '58px', borderRadius: '16px', border: 'none',
  background: ACCENT, color: '#fff', fontSize: '16px', fontWeight: 700,
  cursor: 'pointer', letterSpacing: '0.2px', touchAction: 'manipulation',
  fontFamily: 'inherit'
};
